import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  Slider,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../contexts/CurrencyContext';
import api from '../../services/api';

export const BUCKET_COLOR = { needs: '#0f766e', wants: '#b45309', savings: '#1d4ed8' };

export function Card({ children, sx }) {
  return (
    <Box sx={{ p: { xs: 1.75, sm: 2.25 }, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', ...sx }}>
      {children}
    </Box>
  );
}

function Title({ title, lead }) {
  return (
    <Box sx={{ mb: 1.25 }}>
      <Typography fontWeight={800}>{title}</Typography>
      {lead && <Typography variant="caption" color="text.secondary" component="div">{lead}</Typography>}
    </Box>
  );
}

function Stat({ label, value, color }) {
  return (
    <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: 'action.hover', minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" component="div" noWrap>{label}</Typography>
      <Typography fontWeight={800} sx={{ color }} noWrap>{value}</Typography>
    </Box>
  );
}

function useDates() {
  const { i18n } = useTranslation();
  return useCallback((iso, opts = { day: 'numeric', month: 'short' }) => {
    if (!iso) return '';
    return new Intl.DateTimeFormat(i18n.language, opts).format(new Date(iso));
  }, [i18n.language]);
}

const pct = (n) => `${Math.round((Number(n) || 0) * 100)}%`;

/** 1. Money between paydays. */
export function PayCycleCard({ cycle }) {
  const { t } = useTranslation();
  const { formatAmount } = useCurrency();
  const date = useDates();
  if (!cycle) return null;
  const irregular = cycle.frequency === 'irregular';
  const per = cycle.perPaycheck || {};

  return (
    <Card>
      <Title
        title={t('paycheck.cycle.title', 'Until your next paycheck')}
        lead={irregular
          ? t('paycheck.cycle.leadIrregular', 'No fixed payday, so this looks at the next 30 days.')
          : cycle.nextPayday
            ? t('paycheck.cycle.lead', { date: date(cycle.nextPayday, { weekday: 'long', day: 'numeric', month: 'long' }), count: cycle.daysToPayday, defaultValue: 'Next payday {{date}}, in {{count}} days.' })
            : t('paycheck.cycle.setup', 'Add your payday above to see the bills due before it.')}
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }, gap: 1 }}>
        <Stat label={t('paycheck.cycle.cash', 'Cash now')} value={formatAmount(cycle.liquid)} />
        <Stat label={t('paycheck.cycle.dueBefore', 'Bills before payday')} value={formatAmount(cycle.dueBefore)} />
        <Stat
          label={t('paycheck.cycle.safe', 'Safe to spend')}
          value={formatAmount(cycle.safeToSpend)}
          color={cycle.short > 0 ? 'error.main' : 'success.main'}
        />
      </Box>

      {cycle.short > 0 && (
        <Alert severity="error" sx={{ mt: 1.25 }}>
          {t('paycheck.cycle.short', { amount: formatAmount(cycle.short), defaultValue: 'Bills before payday are {{amount}} more than your cash. Move a payment date or move money now.' })}
        </Alert>
      )}
      {cycle.holdBack > 0 && (
        <Alert severity="warning" sx={{ mt: 1.25 }}>
          {t('paycheck.cycle.holdBack', { amount: formatAmount(cycle.holdBack), defaultValue: 'Bills right after payday need more than one paycheck covers. Keep {{amount}} back from this one.' })}
        </Alert>
      )}

      {!!cycle.billsBefore?.length && (
        <Stack spacing={0.5} sx={{ mt: 1.5 }}>
          {cycle.billsBefore.slice(0, 6).map((b, i) => (
            <Stack key={`${b.name}-${i}`} direction="row" justifyContent="space-between" gap={1}>
              <Typography variant="body2" noWrap>
                <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>{date(b.dueOn)}</Box>
                {b.name}
              </Typography>
              <Typography variant="body2" fontWeight={700}>{formatAmount(b.amount)}</Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {!irregular && cycle.frequency !== 'monthly' && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
            {t('paycheck.cycle.perPay', 'From each paycheck')}
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {['needs', 'wants', 'savings'].map((b) => (
              <Chip key={b} size="small" sx={{ bgcolor: BUCKET_COLOR[b], color: '#fff', fontWeight: 700 }} label={`${t(`paycheck.bucket.${b}`, b)} ${formatAmount(per[b])}`} />
            ))}
          </Stack>
        </Box>
      )}
    </Card>
  );
}

/** 2. Safety net and goals, fed from the savings line. */
export function GoalsCard({ result, onSplit, onCreated }) {
  const { t } = useTranslation();
  const { formatAmount } = useCurrency();
  const date = useDates();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const em = result?.emergency;
  const goals = result?.goals;
  if (!em) return null;
  const rows = goals?.split?.rows || [];
  const hasGoal = !!goals?.emergencyGoal;

  const create = async () => {
    setBusy(true);
    setError('');
    try {
      const emRow = rows.find((r) => r.emergency);
      await api.post('/paycheck/goals/emergency', {
        target: em.target,
        gap: em.gap,
        monthly: emRow ? emRow.amount : result.buckets.savings,
        name: t('paycheck.goals.emergencyName', 'Emergency fund'),
      });
      onCreated?.();
    } catch (_) {
      setError(t('paycheck.goals.error', 'Could not create the goal. Try again in a moment.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <Title
        title={t('paycheck.safetyTitle', 'Safety net')}
        lead={t('paycheck.safetyLead', { target: formatAmount(em.target), full: formatAmount(em.full), defaultValue: 'Aim for 3 months of needs ({{target}}); 6 months ({{full}}) is full cover.' })}
      />
      <LinearProgress
        variant="determinate"
        value={Math.min(100, em.target > 0 ? (em.liquid / em.target) * 100 : 0)}
        sx={{ height: 8, borderRadius: 99, mb: 1 }}
        color={em.gap === 0 ? 'success' : 'primary'}
      />
      <Typography variant="body2">
        {em.gap === 0
          ? t('paycheck.safetyDone', { months: em.coveredMonths, defaultValue: 'Covered: your cash lasts about {{months}} months of needs.' })
          : em.monthsToTarget != null
            ? t('paycheck.safetyGap', { have: formatAmount(em.liquid), count: em.monthsToTarget, defaultValue: 'You have {{have}} in cash. At this savings line you reach the target in {{count}} months.' })
            : t('paycheck.safetyNone', { have: formatAmount(em.liquid), defaultValue: 'You have {{have}} in cash. Free up some savings to start the fund.' })}
      </Typography>
      <Box sx={{ mt: 1.25 }}>
        {!goals ? null : hasGoal ? (
          <Chip size="small" color="success" variant="outlined" label={t('paycheck.goals.tracked', 'Tracked as a savings goal')} />
        ) : (
          <Button size="small" variant="outlined" onClick={create} disabled={busy}>
            {busy ? <CircularProgress size={16} /> : t('paycheck.goals.create', { amount: formatAmount(em.target), defaultValue: 'Track it as a goal ({{amount}})' })}
          </Button>
        )}
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </Box>

      {!!rows.length && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight={800}>
            {t('paycheck.goals.splitTitle', { amount: formatAmount(result.buckets.savings), defaultValue: 'Where your {{amount}} of savings goes' })}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
            {t('paycheck.goals.splitLead', 'The safety net comes first. Other goals share the rest by what they need to arrive on time.')}
          </Typography>
          <Stack spacing={1.25}>
            {rows.map((r) => {
              const key = r.emergency ? 'emergency' : r.id;
              const max = Math.max(r.amount, result.buckets.savings);
              return (
                <Box key={key}>
                  <Stack direction="row" justifyContent="space-between" gap={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={700} noWrap>
                        {r.emergency ? t('paycheck.goals.emergencyName', 'Emergency fund') : r.name}
                      </Typography>
                      <Typography variant="caption" color={r.onTrack === false ? 'warning.main' : 'text.secondary'} component="div">
                        {r.monthsToGoal
                          ? t('paycheck.goals.eta', { count: r.monthsToGoal, left: formatAmount(r.remaining), defaultValue: '{{left}} to go · about {{count}} months' })
                          : t('paycheck.goals.nothing', { left: formatAmount(r.remaining), defaultValue: '{{left}} to go · nothing set aside' })}
                        {r.onTrack === false && r.targetDate && ` · ${t('paycheck.goals.late', { date: date(r.targetDate, { month: 'short', year: 'numeric' }), need: formatAmount(r.perMonthNeeded), defaultValue: 'needs {{need}}/month for {{date}}' })}`}
                      </Typography>
                    </Box>
                    <Typography fontWeight={800}>{formatAmount(r.amount)}</Typography>
                  </Stack>
                  <Slider
                    size="small"
                    value={r.amount}
                    min={0}
                    max={max}
                    step={max > 500 ? 10 : 5}
                    onChangeCommitted={(_, v) => onSplit?.(key, v)}
                    aria-label={r.name}
                  />
                </Box>
              );
            })}
          </Stack>
          {goals.split.unassigned > 0.5 && (
            <Typography variant="caption" color="text.secondary">
              {t('paycheck.goals.unassigned', { amount: formatAmount(goals.split.unassigned), defaultValue: '{{amount}} a month is not assigned to a goal yet.' })}
            </Typography>
          )}
          {goals.split.unassigned < -0.5 && (
            <Typography variant="caption" color="error.main">
              {t('paycheck.goals.overAssigned', { amount: formatAmount(-goals.split.unassigned), defaultValue: 'Goals ask for {{amount}} more than the savings line.' })}
            </Typography>
          )}
        </Box>
      )}
    </Card>
  );
}

/** 3. Debt: minimums only versus an extra payment from savings. */
export function DebtCard({ debt, extra, method, onChange }) {
  const { t } = useTranslation();
  const { formatAmount } = useCurrency();
  const date = useDates();
  const [value, setValue] = useState(extra || 0);
  useEffect(() => { setValue(extra || 0); }, [extra]);
  if (!debt) return null;
  const when = (d) => date(d, { month: 'long', year: 'numeric' });

  return (
    <Card>
      <Title
        title={t('paycheck.debt.title', 'Debt-free date')}
        lead={t('paycheck.debt.lead', { count: debt.count, total: formatAmount(debt.total), defaultValue: '{{count}} debts, {{total}} owed. Extra payments come out of savings, never out of needs.' })}
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
        <Stat
          label={t('paycheck.debt.minOnly', 'Minimums only')}
          value={debt.neverPaid ? t('paycheck.debt.never', 'Never') : when(debt.base.date)}
          color={debt.neverPaid ? 'error.main' : undefined}
        />
        <Stat
          label={t('paycheck.debt.withExtra', { amount: formatAmount(debt.extra), defaultValue: 'With {{amount}} extra' })}
          value={when(debt.withExtra.date)}
          color="success.main"
        />
      </Box>
      {debt.neverPaid && (
        <Alert severity="error" sx={{ mt: 1.25 }}>
          {t('paycheck.debt.neverLead', 'The minimum payments barely cover the interest. Any extra payment changes this.')}
        </Alert>
      )}
      {debt.extra > 0 && debt.monthsSaved > 0 && (
        <Typography variant="body2" sx={{ mt: 1.25 }}>
          {t('paycheck.debt.saved', { count: debt.monthsSaved, interest: formatAmount(debt.interestSaved), defaultValue: '{{count}} months sooner and {{interest}} less interest.' })}
        </Typography>
      )}
      {debt.suggested && debt.suggestedExtra > 0 && debt.extra !== debt.suggestedExtra && (
        <Alert
          severity="info"
          sx={{ mt: 1.25 }}
          action={<Button color="inherit" size="small" onClick={() => onChange({ extraDebt: debt.suggestedExtra })}>{t('paycheck.debt.use', 'Use it')}</Button>}
        >
          {t('paycheck.debt.suggest', { amount: formatAmount(debt.suggestedExtra), date: when(debt.suggested.date), defaultValue: 'Try {{amount}} extra a month: debt-free by {{date}}.' })}
        </Alert>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'center' }} sx={{ mt: 1.5 }}>
        <TextField
          size="small"
          type="number"
          label={t('paycheck.debt.extra', 'Extra each month')}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => Number(value || 0) !== Number(extra || 0) && onChange({ extraDebt: Number(value) || 0 })}
          inputProps={{ min: 0, step: 10, inputMode: 'decimal' }}
          sx={{ maxWidth: { sm: 180 } }}
        />
        <ToggleButtonGroup exclusive size="small" value={method} onChange={(_, v) => v && onChange({ debtMethod: v })}>
          <ToggleButton value="avalanche">{t('paycheck.debt.avalanche', 'Highest interest first')}</ToggleButton>
          <ToggleButton value="snowball">{t('paycheck.debt.snowball', 'Smallest balance first')}</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
    </Card>
  );
}

/** 7. Yearly costs set aside monthly. */
export function AnnualCard({ annualCosts, line }) {
  const { t } = useTranslation();
  const { formatAmount } = useCurrency();
  const items = annualCosts?.items || [];
  if (!items.length && !(line?.suggested > 0)) return null;
  return (
    <Card>
      <Title
        title={t('paycheck.annual.title', 'Yearly costs')}
        lead={t('paycheck.annual.lead', { amount: formatAmount(line?.suggested || 0), defaultValue: 'Set aside {{amount}} a month so yearly bills are already paid for when they arrive.' })}
      />
      <Stack spacing={0.5}>
        {items.slice(0, 8).map((i, n) => (
          <Stack key={`${i.name}-${n}`} direction="row" justifyContent="space-between" gap={1}>
            <Typography variant="body2" noWrap>
              {i.name}
              <Box component="span" sx={{ color: 'text.secondary', ml: 1 }}>
                {formatAmount(i.amount)} / {t(`paycheck.annual.${i.frequency}`, i.frequency === 'yearly' ? 'year' : 'quarter')}
              </Box>
            </Typography>
            <Typography variant="body2" fontWeight={700}>{formatAmount(i.perMonth)}</Typography>
          </Stack>
        ))}
      </Stack>
      {!items.length && (
        <Typography variant="caption" color="text.secondary">
          {t('paycheck.annual.manual', 'Entered by you. Add yearly bills as recurring transactions to fill this in.')}
        </Typography>
      )}
    </Card>
  );
}

/** 8. Irregular income: plan on a lean month, park the surplus. */
export function SurplusCard({ surplus, profile, income }) {
  const { t } = useTranslation();
  const { formatAmount } = useCurrency();
  if (!surplus) return null;
  return (
    <Card>
      <Title
        title={t('paycheck.surplus.title', 'When a month pays more')}
        lead={t('paycheck.surplus.lead', { amount: formatAmount(income), defaultValue: 'This plan is built on a lean month of {{amount}}. Anything above it goes in this order:' })}
      />
      <Stack spacing={0.75}>
        <Typography variant="body2">
          {surplus.bufferGap > 0
            ? t('paycheck.surplus.step1', { amount: formatAmount(surplus.bufferGap), target: formatAmount(surplus.bufferTarget), defaultValue: '1. Buffer: {{amount}} more to reach one month of spending ({{target}}).' })
            : t('paycheck.surplus.step1Done', { target: formatAmount(surplus.bufferTarget), defaultValue: '1. Buffer: done, you hold a month of spending ({{target}}).' })}
        </Typography>
        <Typography variant="body2">{t('paycheck.surplus.step2', '2. Then your goals, in the order shown in the savings split.')}</Typography>
      </Stack>
      {profile?.lowest != null && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
          {t('paycheck.surplus.range', { low: formatAmount(profile.lowest), high: formatAmount(profile.highest), count: profile.months, defaultValue: 'Last {{count}} months ranged from {{low}} to {{high}}.' })}
        </Typography>
      )}
    </Card>
  );
}

/** 10. Shared household costs. */
export function SharedCard({ shared }) {
  const { t } = useTranslation();
  const { formatAmount } = useCurrency();
  if (!shared) return null;
  return (
    <Card>
      <Title
        title={t('paycheck.shared.title', 'Shared costs')}
        lead={t('paycheck.shared.lead', { you: pct(shared.share), partner: pct(1 - shared.share), defaultValue: 'Split by take-home: you {{you}}, your partner {{partner}}.' })}
      />
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& td, & th': { py: 0.5, fontSize: 14 }, '& th': { color: 'text.secondary', fontWeight: 600, textAlign: 'right' }, '& td:not(:first-of-type)': { textAlign: 'right' } }}>
        <thead>
          <tr>
            <Box component="th" sx={{ textAlign: 'left !important' }} />
            <th>{t('paycheck.shared.total', 'Total')}</th>
            <th>{t('paycheck.shared.you', 'You')}</th>
            <th>{t('paycheck.shared.partner', 'Partner')}</th>
          </tr>
        </thead>
        <tbody>
          {shared.lines.map((l) => (
            <tr key={l.key}>
              <td>{t(`paycheck.line.${l.key}`, l.key)}</td>
              <td>{formatAmount(l.total)}</td>
              <Box component="td" sx={{ fontWeight: 700 }}>{formatAmount(l.you)}</Box>
              <td>{formatAmount(l.partner)}</td>
            </tr>
          ))}
          <Box component="tr" sx={{ '& td': { borderTop: '1px solid', borderColor: 'divider', fontWeight: 800 } }}>
            <td>{t('paycheck.shared.sum', 'Each month')}</td>
            <td>{formatAmount(shared.total)}</td>
            <td>{formatAmount(shared.you)}</td>
            <td>{formatAmount(shared.partner)}</td>
          </Box>
        </tbody>
      </Box>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
        {t('paycheck.shared.note', 'Your plan above already uses your share of the rent. Track shared bills in SplitBill to settle up.')}
      </Typography>
    </Card>
  );
}

const PRESETS = [
  { key: 'raise', label: '+5% pay', apply: (f) => ({ income: Math.round(Number(f.income) * 1.05 * 100) / 100 }) },
  { key: 'cheaper', label: 'Cheaper city', when: (f) => Number(f.tier) > 1, apply: (f) => ({ tier: Number(f.tier) - 1 }) },
  { key: 'pricier', label: 'Pricier city', when: (f) => Number(f.tier) < 4, apply: (f) => ({ tier: Number(f.tier) + 1 }) },
  { key: 'noCar', label: 'No car', when: (f) => f.car, apply: () => ({ car: false }) },
  { key: 'car', label: 'Buy a car', when: (f) => !f.car, apply: () => ({ car: true }) },
  { key: 'person', label: 'One more person', apply: (f) => ({ household: Number(f.household || 1) + 1 }) },
];

/** 6. What-if: the plan next to a changed version of it. */
export function ScenarioCard({ body }) {
  const { t } = useTranslation();
  const { formatAmount } = useCurrency();
  const [active, setActive] = useState(null);
  const [rent, setRent] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const run = async (key, changes) => {
    setActive(key);
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/paycheck/scenario', { ...body, changes }, { timeout: 20000 });
      setData(res.data?.data || null);
    } catch (_) {
      setError(t('paycheck.scenario.error', 'Could not compare. Try again in a moment.'));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  if (!body) return null;
  const rows = data ? [
    ['needs', (s) => formatAmount(s.buckets.needs)],
    ['wants', (s) => formatAmount(s.buckets.wants)],
    ['savings', (s) => formatAmount(s.buckets.savings)],
    ['rate', (s) => pct(s.anyActuals ? s.savingsShare : s.planSavingsShare)],
    ['safety', (s) => (s.emergencyMonths == null ? '—' : s.emergencyMonths === 0 ? t('paycheck.scenario.done', 'done') : t('paycheck.scenario.months', { count: s.emergencyMonths, defaultValue: '{{count}} mo' }))],
  ] : [];
  const label = {
    needs: t('paycheck.bucket.needs', 'Needs'),
    wants: t('paycheck.bucket.wants', 'Wants'),
    savings: t('paycheck.bucket.savings', 'Savings'),
    rate: t('paycheck.scenario.rate', 'Saving rate'),
    safety: t('paycheck.scenario.safety', 'Safety net in'),
  };

  return (
    <Card>
      <Title
        title={t('paycheck.scenario.title', 'What if…')}
        lead={t('paycheck.scenario.lead', 'Try a change and see the plan next to today’s. Nothing is saved.')}
      />
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {PRESETS.filter((p) => !p.when || p.when(body)).map((p) => (
          <Chip
            key={p.key}
            label={t(`paycheck.scenario.${p.key}`, p.label)}
            color={active === p.key ? 'primary' : 'default'}
            onClick={() => run(p.key, p.apply(body))}
            disabled={loading}
          />
        ))}
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
        <TextField
          size="small"
          type="number"
          label={t('paycheck.scenario.rent', 'Different rent')}
          value={rent}
          onChange={(e) => setRent(e.target.value)}
          inputProps={{ min: 0, step: 10, inputMode: 'decimal' }}
        />
        <Button variant="outlined" disabled={loading || rent === ''} onClick={() => run('rent', { rent: Number(rent) })}>
          {t('paycheck.scenario.try', 'Compare')}
        </Button>
      </Stack>
      {loading && <LinearProgress sx={{ mt: 1.5 }} />}
      {error && <Alert severity="error" sx={{ mt: 1.25 }}>{error}</Alert>}
      {data && !loading && (
        <Box sx={{ mt: 1.5 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', gap: 0.5, alignItems: 'center' }}>
            <span />
            <Typography variant="caption" fontWeight={800} textAlign="right">{t('paycheck.scenario.now', 'Today')}</Typography>
            <Typography variant="caption" fontWeight={800} textAlign="right" color="primary.main">{t('paycheck.scenario.then', 'What if')}</Typography>
            {rows.map(([key, fmt]) => (
              <React.Fragment key={key}>
                <Typography variant="body2" color="text.secondary">{label[key]}</Typography>
                <Typography variant="body2" textAlign="right">{fmt(data.base)}</Typography>
                <Typography variant="body2" textAlign="right" fontWeight={800}>{fmt(data.scenario)}</Typography>
              </React.Fragment>
            ))}
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.25 }}>
            <Typography variant="body2" color="text.secondary">{t('paycheck.scenario.verdict', 'Verdict')}:</Typography>
            <Chip size="small" color={data.scenario.verdict.level === 'success' ? 'success' : data.scenario.verdict.level === 'error' ? 'error' : data.scenario.verdict.level === 'warning' ? 'warning' : 'default'} label={t(`paycheck.verdict.${data.scenario.verdict.code}`, data.scenario.verdict.code)} />
          </Stack>
          {data.scenario.anyActuals && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.75 }}>
              {t('paycheck.scenario.habits', 'Judged against your current habits, except the lines the change replaces.')}
            </Typography>
          )}
        </Box>
      )}
    </Card>
  );
}

/** 5. Keep the plan every month, and the mid-month check-in. */
export function AutomationCard({ settings, onSaved }) {
  const { t } = useTranslation();
  const [state, setState] = useState({ autoApply: !!settings?.autoApply, checkIn: settings?.checkIn !== false });
  const [error, setError] = useState('');
  useEffect(() => {
    setState({ autoApply: !!settings?.autoApply, checkIn: settings?.checkIn !== false });
  }, [settings?.autoApply, settings?.checkIn]);

  const toggle = (key) => async (e) => {
    const value = e.target.checked;
    setState((s) => ({ ...s, [key]: value }));
    setError('');
    try {
      await api.patch('/paycheck/settings', { [key]: value });
      onSaved?.({ [key]: value });
    } catch (_) {
      setState((s) => ({ ...s, [key]: !value }));
      setError(t('paycheck.automation.error', 'Could not save the setting.'));
    }
  };

  return (
    <Stack spacing={0.5} sx={{ mt: 1 }}>
      <FormControlLabel
        control={<Switch checked={state.autoApply} onChange={toggle('autoApply')} />}
        label={(
          <Box>
            <Typography variant="body2" fontWeight={700}>{t('paycheck.automation.apply', 'Keep this plan every month')}</Typography>
            <Typography variant="caption" color="text.secondary">{t('paycheck.automation.applyLead', 'On the 1st, the plan is rebuilt from your latest spending and saved as that month’s budgets.')}</Typography>
          </Box>
        )}
      />
      <FormControlLabel
        control={<Switch checked={state.checkIn} onChange={toggle('checkIn')} />}
        label={(
          <Box>
            <Typography variant="body2" fontWeight={700}>{t('paycheck.automation.checkIn', 'Mid-month check-in')}</Typography>
            <Typography variant="caption" color="text.secondary">{t('paycheck.automation.checkInLead', 'One note around the 15th on the categories running ahead of plan.')}</Typography>
          </Box>
        )}
      />
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
}

/** 9. Applied plans over time, with undo for the latest one. */
export function HistoryCard({ refreshKey, onUndone }) {
  const { t } = useTranslation();
  const { formatAmount } = useCurrency();
  const date = useDates();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await api.get('/paycheck/history', { timeout: 15000 });
      setRows(res.data?.data || []);
    } catch (_) {
      setRows([]);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const undo = async (id) => {
    setBusy(id);
    setError('');
    try {
      await api.post(`/paycheck/history/${id}/undo`);
      await load();
      onUndone?.();
    } catch (_) {
      setError(t('paycheck.undoError', 'Could not undo. Check the Budgets page.'));
    } finally {
      setBusy(null);
    }
  };

  if (!rows || !rows.length) return null;
  const live = rows.filter((r) => !r.undone && r.planSavingsShare != null);
  const first = live[live.length - 1];
  const last = live[0];
  const monthName = (key) => {
    const [y, m] = key.split('-').map(Number);
    return date(new Date(y, m - 1, 1).toISOString(), { month: 'short', year: 'numeric' });
  };

  return (
    <Card>
      <Title
        title={t('paycheck.history.title', 'Plan history')}
        lead={first && last && first !== last
          ? t('paycheck.history.trend', { from: pct(first.planSavingsShare), to: pct(last.planSavingsShare), month: monthName(first.month), defaultValue: 'Planned saving rate went from {{from}} to {{to}} since {{month}}.' })
          : t('paycheck.history.lead', 'Every time a plan is saved as budgets it shows up here.')}
      />
      <Stack spacing={0.75}>
        {rows.slice(0, 8).map((r) => (
          <Stack key={r.id} direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ opacity: r.undone ? 0.5 : 1 }}>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Typography variant="body2" fontWeight={700}>{monthName(r.month)}</Typography>
                {r.auto && <Chip size="small" variant="outlined" label={t('paycheck.history.auto', 'auto')} sx={{ height: 18, fontSize: 11 }} />}
                {r.undone && <Chip size="small" variant="outlined" label={t('paycheck.history.undone', 'undone')} sx={{ height: 18, fontSize: 11 }} />}
              </Stack>
              <Typography variant="caption" color="text.secondary" component="div" noWrap>
                {r.income != null && `${formatAmount(r.income)} · `}
                {t('paycheck.history.saving', { pct: pct(r.planSavingsShare), defaultValue: 'saving {{pct}}' })}
                {r.verdict && ` · ${t(`paycheck.verdict.${r.verdict}`, r.verdict)}`}
              </Typography>
            </Box>
            {r.canUndo && (
              <Button size="small" onClick={() => undo(r.id)} disabled={busy === r.id}>
                {busy === r.id ? <CircularProgress size={14} /> : t('paycheck.undo', 'Undo')}
              </Button>
            )}
          </Stack>
        ))}
      </Stack>
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </Card>
  );
}
